import React from "react";
import styled, { css, keyframes } from "styled-components";
import { Typography } from "../../../components/Typography";

export const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const shimmerGradient = css`
  background: linear-gradient(
    90deg,
    rgba(24, 31, 56, 0.06) 0%,
    rgba(24, 31, 56, 0.12) 50%,
    rgba(24, 31, 56, 0.06) 100%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.4s infinite;
`;

// ─── Pseudo-element overlay ────────────────────────────────────────────────
// Drop onto a positioned container to layer a shimmer sweep over it.
export const shimmerOverlay = css`
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 8px;
    opacity: 1;
    pointer-events: none;
    transition: opacity 0.2s ease-in-out;
    ${shimmerGradient}
  }
`;

// ─── Word pill ─────────────────────────────────────────────────────────────
const WORD_WIDTHS = ["3.5em", "4.8em", "2.8em", "5.2em", "3em", "4em"];
const LONG_WORD_WIDTHS = [
  "3.1em",
  "5.8em",
  "4.2em",
  "6.6em",
  "2.4em",
  "5.1em",
  "7.4em",
  "3.3em",
  "6.1em",
  "4.6em",
  "2.8em",
  "5.9em",
  "3.7em",
  "7.8em",
  "4.1em",
  "2.5em",
  "5.4em",
  "6.8em",
  "3.2em",
  "4.9em",
  "7.1em",
  "2.6em",
  "5.6em",
  "3.4em",
  "2.9em",
];

const ShimmerPill = styled.span`
  display: inline-block;
  font-size: inherit;
  width: ${(props) => props.$w};
  min-width: 2em;
  height: 0.75em;
  border-radius: 0.25em;
  vertical-align: middle;
  margin-bottom: 0.1em;
  ${shimmerGradient}
`;

const ShimmerGap = styled.span`
  display: inline-block;
  font-size: inherit;
  width: 0.4em;
`;

// ─── ShimmerText component ─────────────────────────────────────────────────
// Props:
//   words  – number of word pills to render (default 1)
//   long   – shorthand for a paragraph-style placeholder (~25 words)
export const ShimmerText = ({
  words,
  long,
  tag = "p",
  fontSize = "16",
  mb,
  mt,
  ml,
  mr,
  mbLG,
  nomargin,
  large,
  color,
  small,
}) => {
  const widths = long ? LONG_WORD_WIDTHS : WORD_WIDTHS;
  const count = long
    ? LONG_WORD_WIDTHS.length
    : Math.max(Number(words) || 1, 1);

  return (
    <>
      <Typography
        tag={tag}
        fontSize={fontSize}
        mb={mb}
        mt={mt}
        ml={ml}
        mr={mr}
        mbLG={mbLG}
        nomargin={nomargin}
        large={large}
        color={color}
        small={small}
      >
        {Array.from({ length: count }, (_, i) => (
          <React.Fragment key={i}>
            {i > 0 && <ShimmerGap />}
            <ShimmerPill $w={widths[i % widths.length]} />
          </React.Fragment>
        ))}
      </Typography>
    </>
  );
};
